// Pantheon spear throw VFX: an elongated spear with shaft, cross-guard and tip,
// oriented along the travel direction and landing with a sharp impact ring.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const TRAVEL_MS = 200;
const IMPACT_MS = 180;
const TOTAL = TRAVEL_MS + IMPACT_MS;

const SHAFT_LEN = 30;
const SHAFT_W   = 3;
const HEAD_LEN  = 11;
const HEAD_W    = 5;
const GUARD_W   = 4;
const GUARD_H   = 13;

export class PantheonSpearVFX implements Animation {
  private elapsed = 0;
  private angle: number;
  private impactFired = false;

  constructor(
    private from: Point,
    private to: Point,
    private onImpact?: () => void,
  ) {
    this.angle = Math.atan2(to.y - from.y, to.x - from.x);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (!this.impactFired && this.elapsed >= TRAVEL_MS) {
      this.impactFired = true;
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed <= TRAVEL_MS) {
      this.drawTravel(ctx);
    } else {
      this.drawImpact(ctx);
    }
  }

  private drawTravel(ctx: CanvasRenderingContext2D): void {
    const t = Easing.easeOutQuad(this.elapsed / TRAVEL_MS);
    const x = this.from.x + (this.to.x - this.from.x) * t;
    const y = this.from.y + (this.to.y - this.from.y) * t;

    // Faint motion trail
    const trailT = Math.max(0, t - 0.2);
    const trailX = this.from.x + (this.to.x - this.from.x) * trailT;
    const trailY = this.from.y + (this.to.y - this.from.y) * trailT;
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = "#c8a060";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(trailX, trailY);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.translate(x, y);
    // Rotate so tip points in the travel direction (+X in local space = forward)
    ctx.rotate(this.angle);
    ctx.shadowColor = "#c8a060";
    ctx.shadowBlur = 12;

    // Shaft (wooden handle)
    ctx.fillStyle = "#8a6020";
    ctx.fillRect(-SHAFT_LEN / 2, -SHAFT_W / 2, SHAFT_LEN, SHAFT_W);

    // Cross-guard
    ctx.fillStyle = "#c0a050";
    ctx.shadowBlur = 6;
    ctx.fillRect(
      SHAFT_LEN / 2 - HEAD_LEN - GUARD_W,
      -GUARD_H / 2,
      GUARD_W,
      GUARD_H,
    );

    // Spearhead — diamond pointing right (+X)
    ctx.fillStyle = "#d4c080";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.moveTo(SHAFT_LEN / 2 + HEAD_LEN, 0);          // tip
    ctx.lineTo(SHAFT_LEN / 2 - HEAD_LEN * 0.1, -HEAD_W / 2); // top base
    ctx.lineTo(SHAFT_LEN / 2 - HEAD_LEN * 0.3, 0);    // socket
    ctx.lineTo(SHAFT_LEN / 2 - HEAD_LEN * 0.1, HEAD_W / 2);  // bottom base
    ctx.closePath();
    ctx.fill();

    // Tip glint
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(SHAFT_LEN / 2 + HEAD_LEN * 0.85, 0, 1.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D): void {
    const t = (this.elapsed - TRAVEL_MS) / IMPACT_MS;
    const fadeOut = 1 - t;
    const ringT = Easing.easeOutQuad(t);

    ctx.save();
    ctx.shadowColor = "#c8a060";
    ctx.shadowBlur = 18;

    // Tight impact ring
    const ringR = 9 * (1 + ringT * 3.5);
    ctx.globalAlpha = fadeOut * 0.85;
    ctx.strokeStyle = "#d4c080";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.to.x, this.to.y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // White flash
    const flashR = 7 * (1.2 - t * 1.2);
    if (flashR > 0) {
      ctx.globalAlpha = fadeOut * 0.9;
      ctx.fillStyle = "#ffffff";
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(this.to.x, this.to.y, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
